/**
 * Per-line "auto-size the font to fit" hook for matrix cells.
 *
 * Each line of a matrix cell (device-first, device-rest, control) has a
 * fixed 8px default font-size. Most labels fit; long control names like
 * "Btn 1 — Acoustic Distortion" overflow at narrow viewports and get
 * ellipsis-truncated. This hook measures scrollWidth vs clientWidth per
 * line and, when overflowing, shrinks that line's font-size just enough
 * to fit — with a 6px floor so text never becomes unreadable.
 *
 * Runs on cell mount + on cell resize (ResizeObserver). Cheap: three
 * measurements per cell per resize; no reflow-thrashing since each
 * measurement reads then writes atomically.
 *
 * Tiny safety margin: the calc leaves 2px slack in the target width so
 * we don't end up sitting exactly at ratio 1.00 where sub-pixel drift
 * can flick a line back into overflow.
 */

const DEFAULT_PX = 8;
// 5px still reads at desktop / iPad viewing distance, and lets the
// extreme long-tail cases like "Btn 1 — Acoustic Distortion" (27 chars)
// fit inside a 90px column interior. Below 5px, text starts to become
// unreadable — ellipsis is preferable at that point.
const FLOOR_PX = 5;
// Bigger safety margin than sub-pixel drift + integer-floor residue so
// we always land inside the column with a whisker of breathing room.
const SAFETY_PX = 4;

const LINE_SELECTORS = [
  '.matrix-grid-cell-label-device-first',
  '.matrix-grid-cell-label-device-rest',
  '.matrix-grid-cell-label-control',
] as const;

function fitOneLine(el: HTMLElement): void {
  // Reset first so a widening resize can grow text back up to the
  // default. Otherwise once shrunk on a narrow viewport, a subsequent
  // resize wider wouldn't recover to 8px.
  el.style.fontSize = '';
  // Read after write in the same layout batch — modern browsers coalesce
  // this since we just cleared inline style; the getBoundingClientRect
  // still triggers a synchronous style flush.
  const need = el.scrollWidth;
  const have = el.clientWidth;
  if (need <= have) return;
  // available / needed × current-font-size gives the target size that
  // exactly fills the line. Subtract safety, floor at 6px.
  const targetSize = Math.max(
    FLOOR_PX,
    Math.floor(((have - SAFETY_PX) / need) * DEFAULT_PX),
  );
  if (targetSize < DEFAULT_PX) {
    el.style.fontSize = `${targetSize}px`;
  }
}

/** Apply the fit-to-content pass to every line inside one cell. */
export function fitCell(cell: HTMLElement): void {
  for (const selector of LINE_SELECTORS) {
    const el = cell.querySelector<HTMLElement>(selector);
    if (el) fitOneLine(el);
  }
}

/** Attach a ResizeObserver + one-shot initial fit to a cell element.
 *  Returns a disposer for onCleanup. The observer refires whenever the
 *  cell's dimensions change (viewport resize, modal open, sticky-header
 *  scroll adjusting content-box width). */
export function attachAutosize(cell: HTMLElement): () => void {
  // Initial pass — content may not be laid out yet on first paint;
  // defer to next microtask so scrollWidth is populated.
  queueMicrotask(() => fitCell(cell));
  const ro = new ResizeObserver(() => fitCell(cell));
  ro.observe(cell);
  return () => ro.disconnect();
}
